import { useEffect, useRef, useState } from "react";

/**
 * Animates a number from 0 up to `end` when it first mounts,
 * using requestAnimationFrame for a smooth, frame-accurate count.
 *
 * Props:
 * - end: Final value to count up to
 * - duration: Animation duration in milliseconds
 * - prefix: Text shown before the number, e.g. "₹"
 * - suffix: Text shown after the number, e.g. "+"
 * - format: Format the number (defaults to en-IN grouping, e.g. 75,000)
 */
const CountUp = ({
  end,
  duration = 1500,
  prefix = "",
  suffix = "",
  format = (v) => v.toLocaleString("en-IN"),
}) => {
  const [value, setValue] = useState(0);
  const frameRef = useRef(null);
  const startTimeRef = useRef(null);

  useEffect(() => {
    // easeOutCubic — fast at the start, slows near the end
    const easeOut = (t) => 1 - Math.pow(1 - t, 3);

    const tick = (now) => {
      if (startTimeRef.current === null) startTimeRef.current = now;
      const elapsed = now - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);

      setValue(Math.round(easeOut(progress) * end));

      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick);
      }
    };

    frameRef.current = requestAnimationFrame(tick);

    return () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      startTimeRef.current = null;
    };
  }, [end, duration]);

  return (
    <span>
      {prefix}
      {format(value)}
      {suffix}
    </span>
  );
};

export default CountUp;
